import React, { useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import { withTranslation, Trans } from 'react-i18next';

import { makeGetTaskActiveSubtasks } from 'selectors/tasks';
import { makeGetFetchedState, makeGetLoadingState } from 'selectors/global';

import { fetchTasks } from 'actions/tasks';
import { showModal } from 'actions/modal';

import { capitalize } from 'utils/helpers';

import SelectTaskGroupPopout from 'components/SelectTaskGroupPopout';
import MilestoneName from 'components/milestone-name';
import TaskName from 'components/task-name';
import Tooltip from 'components/tooltip';
import Checkbox from 'components/checkbox';

import IconLock from 'icons/icon-lock';

import './TaskCompleteToggle.styles.css';

const TaskCompleteToggle = ({ id, disabled, dark, completed, locked, dependantOn, projectId, size = 'default', selectListDisabled, tickOnly, checkSubtasks, activeSubtasks, subtasksFetched, subtasksLoading, onChange, t }) => {
    const isDisabled = useMemo(() => Boolean(disabled || locked || dependantOn), [disabled, locked, dependantOn]);

    const handleCompletedToggle = useCallback(async state => {
        if (!isDisabled && (!selectListDisabled && completed && projectId)) {
            return;
        }

        if (state && checkSubtasks && id) {
            if (!subtasksFetched) {
                await fetchTasks({ taskId: id }, true);
            }

            if (activeSubtasks.length) {
                showModal('COMPLETE_SUBTASKS', {
                    subtasks: activeSubtasks,
                    onContinue: () => {
                        onChange && onChange(state);
                    }
                });
                return;
            }
        }

        onChange && onChange(state);
    }, [isDisabled, selectListDisabled, completed, projectId, checkSubtasks, subtasksFetched, id, activeSubtasks, onChange]);

    const handleTaskGroupSelect = useCallback(({ id }) => {
        onChange && onChange(false, id);
    }, [onChange]);

    const tooltipContent = useMemo(() => {
        if (dependantOn) {
            return (
                <Trans
                    i18nKey="task:Dependant on"
                    values={{
                        type: dependantOn.type === 'milestone' ? t('milestone:Milestone').toLowerCase() : t('Task').toLowerCase()
                    }}
                    components={{
                        name: dependantOn.type === 'milestone' ? <MilestoneName id={dependantOn.id} /> : <TaskName id={dependantOn.id} />
                    }}
                />
            );
        }

        if (completed) {
            return t('actions.Mark task uncomplete');
        }

        return t('actions.Mark task complete');
    }, [completed, dependantOn, t]);

    const checkboxElement = useMemo(() => (
        <Checkbox
            disabled={isDisabled}
            forceUseProp={true}
            alwaysTick={true}
            tickOnly={tickOnly}
            inverted={true}
            dark={dark}
            checked={completed}
            onChange={handleCompletedToggle}
        />
    ), [isDisabled, dark, completed, handleCompletedToggle]);

    return (
        <Tooltip showDelay={1000} tooltip={tooltipContent} disabled={disabled || locked}>
            <div className={'task-complete-toggle size-' + size}>
                {
                    !selectListDisabled && !isDisabled && completed && projectId ? (
                        <SelectTaskGroupPopout
                            projectId={projectId}
                            withProject={false}
                            onSelect={handleTaskGroupSelect}
                        >
                            <div>
                                { checkboxElement }
                            </div>
                        </SelectTaskGroupPopout>
                    ) : checkboxElement
                }
                

                {
                    locked || dependantOn ? (
                        <span className="dep-icon"><IconLock /></span>
                    ) : null
                }
            </div>
        </Tooltip>
    );
}

const makeMapStateToProps = () => {
    const getTaskActiveSubtasks = makeGetTaskActiveSubtasks();
    const getTasksFetched = makeGetFetchedState();
    const getTasksLoading = makeGetLoadingState();

    return ({ tasks }, ownProps) => ({
        activeSubtasks: ownProps.id && ownProps.checkSubtasks && getTaskActiveSubtasks(tasks.items, ownProps.id),
        subtasksFetched: ownProps.id && ownProps.checkSubtasks && getTasksFetched(tasks.fetchInfo, 'task', ownProps.id),
        subtasksLoading: ownProps.id && ownProps.checkSubtasks && getTasksLoading(tasks.fetchInfo, 'task', ownProps.id)
    });
};

export default connect(makeMapStateToProps)(withTranslation(['task', 'milestone'])(TaskCompleteToggle));
